import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Chip, Collapse, Tooltip, Typography, makeStyles } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import type { FindingResolutionState, SpecIntelligenceFinding } from 'api/graphQL/types/specIntelligence.types'
import clsx from 'clsx'
import React, { useMemo } from 'react'
import { formatLocalDateTime } from 'shared/utils/dateUtils'
import {
    CATEGORY_COLORS,
    CATEGORY_DISPLAY_LABELS,
    SPEC_INT_AI_COLOR,
    SPEC_INT_MUTED_TEXT_COLOR,
    SPEC_INT_NAVY_COLOR,
    SPEC_INT_NEUTRAL_GRAY_COLOR,
    STATE_COLORS,
    STATE_ICONS,
} from './specIntelligenceConstants'

/** Format used for the "on {date} at {time}" suffix of a resolved/dismissed state badge. */
const RESOLUTION_DATE_FORMAT = 'MMM DD, yyyy [at] hh:mm A'

/** Explains the AI badge shown on findings produced by AI-powered analysis. */
const AI_BADGE_TOOLTIP = 'This finding was identified by AI-powered analysis.'

const DEFAULT_CATEGORY_STYLE = { backgroundColor: '#E0E0E0', color: '#424242' }

/** Matches paragraph references such as "[[1.2 > A.]]" embedded in finding descriptions. */
const PARAGRAPH_REF_PATTERN = /\[\[(.+?)\]\]/g

/** Splits a description on paragraph-reference tokens, rendering each as a bold span. */
const renderDescriptionWithParagraphRefs = (description: string, highlightClassName: string): React.ReactNode[] =>
    description.split(PARAGRAPH_REF_PATTERN).map((part, index) =>
        index % 2 === 1 ? <span key={index} className={highlightClassName}>{part}</span> : part
    )

const useStyles = makeStyles(() => ({
    card: {
        borderBottom: '1px solid #EEEEEE',
        padding: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(8)}`,
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#FFF' },
    },
    cardSelected: {
        backgroundColor: '#FFF',
    },
    cardReadOnly: {
        cursor: 'default',
    },
    headerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: pxToRem(8),
        marginBottom: pxToRem(4),
    },
    sectionInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        minWidth: 0,
        flexShrink: 1,
        maxWidth: '65%',
    },
    sectionNumber: {
        fontSize: pxToRem(11),
        fontWeight: 600,
        color: '#424242',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    sectionDot: { fontSize: pxToRem(11), color: SPEC_INT_NEUTRAL_GRAY_COLOR, flexShrink: 0 },
    sectionName: {
        fontSize: pxToRem(11),
        color: SPEC_INT_MUTED_TEXT_COLOR,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        flexShrink: 0,
    },
    expandIconSpacer: {
        marginLeft: 'auto',
    },
    categoryChip: {
        height: pxToRem(20),
        fontSize: pxToRem(10),
        '& .MuiChip-label': { padding: `0 ${pxToRem(6)}` },
    },
    aiBadge: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        height: pxToRem(18),
        borderRadius: pxToRem(4),
        padding: `0 ${pxToRem(6)}`,
        backgroundColor: '#EDE7F6',
        color: SPEC_INT_AI_COLOR,
        fontSize: pxToRem(10),
        fontWeight: 700,
        cursor: 'default',
    },
    stateBadge: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: pxToRem(3),
        flexShrink: 0,
        maxWidth: pxToRem(260),
        minHeight: pxToRem(18),
        borderRadius: pxToRem(4),
        padding: `${pxToRem(3)} ${pxToRem(6)}`,
        fontSize: pxToRem(10),
        fontWeight: 500,
        lineHeight: 1.4,
    },
    stateBadgeIcon: { fontSize: pxToRem(12), display: 'block', flexShrink: 0, marginTop: pxToRem(1) },
    stateBadgeLabel: { display: 'inline-block', whiteSpace: 'normal', wordBreak: 'break-word' },
    stateBadgeName: { fontWeight: 800 },
    expandIcon: {
        fontSize: pxToRem(18),
        color: '#757575',
        transition: 'transform 200ms',
    },
    expandIconOpen: { transform: 'rotate(180deg)' },
    description: {
        fontSize: pxToRem(13),
        color: '#424242',
        lineHeight: 1.5,
    },
    referencedSection: {
        color: '#212121',
        fontWeight: 500,
    },
    actions: {
        display: 'flex',
        gap: pxToRem(8),
        marginTop: pxToRem(10),
    },
    resolveButton: {
        flex: 1,
        textTransform: 'none' as const,
        fontSize: pxToRem(12),
        fontWeight: 500,
        backgroundColor: SPEC_INT_NAVY_COLOR,
        color: '#FFFFFF',
        '&:hover': { backgroundColor: '#02264A' },
    },
    dismissButton: {
        flex: 1,
        textTransform: 'none' as const,
        fontSize: pxToRem(12),
        fontWeight: 500,
        borderColor: SPEC_INT_NEUTRAL_GRAY_COLOR,
        color: '#424242',
    },
    goToSectionLink: {
        display: 'inline-block',
        marginTop: pxToRem(10),
        fontSize: pxToRem(12),
        fontWeight: 500,
        color: SPEC_INT_NAVY_COLOR,
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
    },
}))

interface StateBadgeProps {
    state: FindingResolutionState
    resolvedByName: string | null
    resolvedAt: string | null
}

const StateBadge = ({ state, resolvedByName, resolvedAt }: StateBadgeProps) => {
    const classes = useStyles()
    const StateIcon = STATE_ICONS[state]
    const label =
        resolvedByName && resolvedAt ? (
            <>
                {state} by <span className={classes.stateBadgeName}>{resolvedByName}</span> on{' '}
                {formatLocalDateTime(new Date(resolvedAt), RESOLUTION_DATE_FORMAT)}
            </>
        ) : (
            state
        )
    return (
        <span className={classes.stateBadge} style={STATE_COLORS[state]}>
            <StateIcon className={classes.stateBadgeIcon} />
            <span className={classes.stateBadgeLabel}>{label}</span>
        </span>
    )
}

interface FindingCardHeaderProps {
    finding: SpecIntelligenceFinding
    expanded: boolean
    readOnly?: boolean
}

const FindingCardHeader = ({ finding, expanded, readOnly }: FindingCardHeaderProps) => {
    const classes = useStyles()
    const displayCategory = CATEGORY_DISPLAY_LABELS[finding.category] ?? finding.category
    const chipStyle = CATEGORY_COLORS[finding.category] ?? DEFAULT_CATEGORY_STYLE
    return (
        <div className={classes.headerRow}>
            <div className={classes.sectionInfo}>
                <Typography component="span" className={classes.sectionNumber}>
                    {finding.sectionNumber}
                </Typography>
                <Typography component="span" className={classes.sectionDot}>·</Typography>
                <Tooltip title={finding.sectionName}>
                    <Typography component="span" className={classes.sectionName}>
                        {finding.sectionName}
                    </Typography>
                </Tooltip>
            </div>
            <div className={classes.headerRight}>
                <Chip
                    label={displayCategory}
                    size="small"
                    className={classes.categoryChip}
                    style={chipStyle}
                />
                {finding.isAI && (
                    <Tooltip title={AI_BADGE_TOOLTIP}>
                        <span className={classes.aiBadge}>AI</span>
                    </Tooltip>
                )}
                {readOnly && (
                    <StateBadge
                        state={finding.resolutionState}
                        resolvedByName={finding.resolvedByName}
                        resolvedAt={finding.resolvedAt}
                    />
                )}
            </div>
            {!readOnly && (
                <ExpandMoreIcon
                    className={clsx(classes.expandIcon, classes.expandIconSpacer, expanded && classes.expandIconOpen)}
                    aria-hidden="true"
                />
            )}
        </div>
    )
}

interface FindingCardActionsProps {
    findingId: string
    onResolve: (id: string) => void
    onDismiss: (id: string) => void
}

const FindingCardActions = ({ findingId, onResolve, onDismiss }: FindingCardActionsProps) => {
    const classes = useStyles()
    return (
        <div
            className={classes.actions}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
        >
            <Button
                variant="contained"
                size="small"
                className={classes.resolveButton}
                onClick={() => onResolve(findingId)}
                disableElevation
            >
                Resolve
            </Button>
            <Button
                variant="outlined"
                size="small"
                className={classes.dismissButton}
                onClick={() => onDismiss(findingId)}
            >
                Dismiss
            </Button>
        </div>
    )
}

interface GoToSectionLinkProps {
    projectId: string
    sectionElementId: string
}

const GoToSectionLink = ({ projectId, sectionElementId }: GoToSectionLinkProps) => {
    const classes = useStyles()
    return (
        <a
            href={`/projects/${projectId}/workspace/${sectionElementId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.goToSectionLink}
            onClick={(e) => e.stopPropagation()}
        >
            Go to section →
        </a>
    )
}

interface ReadOnlyFindingCardProps {
    /** The finding to display. */
    finding: SpecIntelligenceFinding
    /**
     * Renders the card without expand/collapse or Resolve/Dismiss actions, showing a
     * "Go to section" link instead (when the finding has a `sectionElementId`). Requires
     * `projectId` to build that link.
     */
    readOnly: true
    /** The current project id, used to build the "Go to section" link. */
    projectId: string
}

interface InteractiveFindingCardProps {
    /** The finding to display. */
    finding: SpecIntelligenceFinding
    readOnly?: false
    /** Called when the user clicks Resolve. */
    onResolve: (id: string) => void
    /** Called when the user clicks Dismiss. */
    onDismiss: (id: string) => void
    /** Whether this card is the currently expanded/selected one. */
    expanded: boolean
    /** Called when the card header is clicked to toggle expansion. */
    onToggle: () => void
}

/** Props for {@link SpecIntelligenceFindingCard}. */
export type SpecIntelligenceFindingCardProps = ReadOnlyFindingCardProps | InteractiveFindingCardProps

/**
 * Collapsed/expanded card for a single SpecIntelligence finding. Expansion is controlled
 * by the parent so only one card in a list can be expanded at a time.
 */
const SpecIntelligenceFindingCard = (props: SpecIntelligenceFindingCardProps) => {
    const classes = useStyles()
    const { finding } = props
    const descriptionNodes = useMemo(
        () => renderDescriptionWithParagraphRefs(finding.description, classes.referencedSection),
        [finding.description, classes.referencedSection]
    )

    if (props.readOnly) {
        const { projectId } = props
        return (
            <div className={clsx(classes.card, classes.cardReadOnly)}>
                <FindingCardHeader finding={finding} expanded={false} readOnly />
                <Typography className={classes.description}>
                    {descriptionNodes}
                </Typography>
                {finding.sectionElementId && (
                    <GoToSectionLink projectId={projectId} sectionElementId={finding.sectionElementId} />
                )}
            </div>
        )
    }

    const { onResolve, onDismiss, expanded, onToggle } = props
    const cleanDescription = finding.description.replace(PARAGRAPH_REF_PATTERN, '$1')

    return (
        <div
            className={clsx(classes.card, expanded && classes.cardSelected)}
            onClick={onToggle}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={`${finding.sectionNumber} · ${finding.sectionName}: ${cleanDescription}`}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggle()
                }
            }}
        >
            <FindingCardHeader finding={finding} expanded={expanded} />
            <Typography className={classes.description}>
                {descriptionNodes}
            </Typography>
            <Collapse in={expanded} unmountOnExit>
                <FindingCardActions findingId={finding.id} onResolve={onResolve} onDismiss={onDismiss} />
            </Collapse>
        </div>
    )
}

export default SpecIntelligenceFindingCard
